import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { wrapArray } from 'app/helpers';
import * as _ from 'lodash';

export interface IDiscountSummaryTicket {
	travellerName: string;
	offerName: string;
	catalogServiceName: string;
	fullPrice: number;
	discountedPrice: number;
	discounted: boolean;
}

export interface IDiscountSummarySolution {
	title: string;
	tickets: IDiscountSummaryTicket[];
}

@Component({
	selector: 'trenitalia-discount-summary-dialog',
	templateUrl: 'trenitalia-discount-summary-dialog.component.html',
	styleUrls: ['trenitalia-discount-summary-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false
})
export class TrenitaliaDiscountSummaryDialogComponent {

	public solutions: IDiscountSummarySolution[] = [];
	public previousTotal = 0;
	public newTotal = 0;
	/** true when the code went through but Trenitalia left every price untouched */
	public noDiscountApplied = true;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: any,
		public dialogRef: MatDialogRef<TrenitaliaDiscountSummaryDialogComponent>
	) {
		this.previousTotal = Number(_.get(data, 'previousTotal', 0));
		this.solutions = wrapArray(_.get(data, 'viewTravelSolutions')).map((solution, index) => {
			return {
				title: this.solutionTitle(solution, index),
				tickets: this.extractTickets(solution)
			};
		});
		this.solutions.forEach(s => s.tickets.forEach(t => {
			this.newTotal += t.discountedPrice;
			if (t.discounted) this.noDiscountApplied = false;
		}));
	}

	private solutionTitle(solution: any, index: number): string {
		if (!_.get(this.data, 'isRoundTrip')) return null;
		return index === 0 ? 'Andata' : 'Ritorno';
	}

	private extractTickets(solution: any): IDiscountSummaryTicket[] {
		const tickets: IDiscountSummaryTicket[] = [];
		const travellers = wrapArray(_.get(this.data, 'travellers'));
		const offeredServices = _.get(solution, 'offeredServices') || {};

		for (const offeredService of Object.values<any>(offeredServices)) {
			if (!['SELECTED', 'PURCHASED'].includes(_.get(offeredService, 'status'))) continue;

			const currentPrice = parseFloat(_.get(offeredService, 'price.amount', 0)) || 0;
			// ORIGINAL_AMOUNT shows up only when the code actually discounted this service.
			// XMLConverter gives back a single object or an array depending on how many
			// attributes there are, so it always goes through wrapArray.
			const attributes = wrapArray(_.get(offeredService, 'original.attributes'));
			const originalAttribute = attributes.find(attribute => _.get(attribute, 'name') === 'ORIGINAL_AMOUNT');
			const originalPrice = originalAttribute ? parseFloat(_.get(originalAttribute, 'value', 0)) : null;
			const discounted = originalPrice !== null && originalPrice !== currentPrice;

			const traveller = travellers.find(t => _.get(t, 'xmlId') === _.get(offeredService, 'travellerXmlId'));

			tickets.push({
				travellerName: traveller ? _.compact([_.get(traveller, 'name'), _.get(traveller, 'surname')]).join(' ') : '',
				offerName: _.get(this.data, ['servicesMap', 'offers', _.get(offeredService, 'offerId'), 'display_name']),
				catalogServiceName: _.get(this.data, ['servicesMap', 'catalog_services', _.get(offeredService, 'catalogServiceId'), 'display_name']),
				fullPrice: discounted ? originalPrice : currentPrice,
				discountedPrice: currentPrice,
				discounted
			});
		}
		return tickets;
	}

	confirm() {
		this.dialogRef.close('ok');
	}

	cancel() {
		this.dialogRef.close('cancel');
	}
}
